import { useState, useEffect, useCallback } from 'react';
import './table.css';

const Table = ({ title = 'AFFILIATE', pointsLabel = 'Points', type = 'affiliate' }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [summary, setSummary] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const DUMMY_DATA = [
    { code: 'AFL-3214', name: 'Ahmed Al-Hassan', revenue: 0, commission: 0 },
    { code: 'AFL-5423', name: 'Sofia Martinez', revenue: 0, commission: 0 },
    { code: 'AFL-767', name: 'Yuki Tanaka', revenue: 0, commission: 0 }
  ];

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log(`🔄 Fetching ${type} data from backend...`);
      
      // Determine endpoint based on type
      let endpoint = `${API_URL}/api/affiliate/stats`;
      if (type === 'influencer') {
        endpoint = `${API_URL}/api/affiliate/stats/art`;
      } else if (type === 'pro') {
        endpoint = `${API_URL}/api/affiliate/stats/pro`;
      }
      
      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Unknown error');
      }

      // Sort by revenue descending
      const sortedData = result.stats.sort((a, b) => b.revenue - a.revenue);

      setData(sortedData);
      setFilteredData(sortedData);
      setSummary(result.summary);
      setLastUpdate(new Date(result.lastUpdate));
      console.log(`✅ Data loaded: ${sortedData.length} codes`);
    } catch (err) {
      console.error('❌ Error loading data:', err);
      setError(err.message);
      // Set dummy data on error for AFL only
      if (type === 'affiliate') {
        setData(DUMMY_DATA);
        setFilteredData(DUMMY_DATA);
      }
    } finally {
      setIsLoading(false);
    }
  }, [API_URL, type]);

  // Handle search
  useEffect(() => {
    const filtered = data.filter(row =>
      row.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when searching
  }, [searchTerm, data]);

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 10 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('⏰ Auto-refresh triggered');
      fetchData();
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchData, REFRESH_INTERVAL]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  if (isLoading && data.length === 0) {
    return (
      <div className="table-container">
        <div className="loading">Loading initial data...</div>
      </div>
    );
  }

  if (error && data.length === 0) {
    return (
      <div className="table-container">
        <div className="error">
          <strong>Error loading data:</strong> {error}
          <br /><br />
          <button onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  const renderFourthColumn = (row, index) => {
    if (type === 'influencer') {
      // For influencer: show rank based on position in sorted data
      return indexOfFirstItem + index + 1;
    } else if (type === 'pro') {
      return `SAR ${row.commission.toFixed(2)}`;
    } else {
      // Default affiliate points: 250 SAR = 1 Point
      return Math.floor(row.revenue / 250);
    }
  };

  return (
    <div className="table-container">
      <div className="table-header">
        <div className="table-title">{title}</div>
        <div className="search-container">
          <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search by code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th data-label="Code">Code</th>
            <th data-label="Revenue">Revenue (SAR)</th>
            <th data-label={pointsLabel}>{pointsLabel}</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((row, index) => (
            <tr key={row.code || index}>
              <td data-label="Code">
                {row.code.toUpperCase()}
              </td>
              <td data-label="Revenue">SAR {row.revenue.toFixed(2)}</td>
              <td data-label={pointsLabel}>{renderFourthColumn(row, index)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {filteredData.length > itemsPerPage && (
        <div className="pagination">
          <button 
            onClick={prevPage} 
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <div className="pagination-info">
            Page {currentPage} of {totalPages} ({filteredData.length} total codes)
          </div>
          
          <button 
            onClick={nextPage} 
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Last Update Info */}
      {lastUpdate && (
        <div className="last-update">
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};

export default Table;